import { createAsyncThunk } from "@reduxjs/toolkit";
import { IStoreState } from "App/types";
import { has, keys } from "lodash-es";
import { Dispatch } from "react";

import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

import { StageService, EquipmentService } from "Venues/services";
import {
  IStage,
  VENUE_ACTIONS,
  IStageCapacity,
  IEquipmentCategory,
  IStagePatchData,
} from "Venues/types";
import {
  getEquipmentCategoriesKey,
  getStageEquipmentKey,
  isPageFetchedInState,
} from "Venues/utils";
import { clearActionPagination } from "Venues/state";

export const createStage = createAsyncThunk(
  VENUE_ACTIONS.createStage,
  async ({ venueId, stage }: { venueId: string; stage: IStage }, { rejectWithValue, dispatch }) => {
    try {
      const { stageCapacities, ...stageData } = stage;
      const createdStage: any = await StageService.createStage(venueId, stageData);

      const stagePath = keys(createdStage.stage)[0];
      const id = (Object.values(createdStage?.stage) as any)?.[0]?.attributes?.id;

      if (stageCapacities) {
        await Promise.all(
          stageCapacities.map(capacity => dispatch(createStageCapacity({ stagePath, capacity })))
        );
      }

      return { id };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetStageAction = (
  dispatch: Dispatch<any>,
  id: string,
  forceFetch?: boolean
) => {
  const action = API_ENDPOINTS.STAGE(id);
  return dispatch(getStage({ id, action, forceFetch }));
};

export const getStage = createAsyncThunk(
  VENUE_ACTIONS.getStage,
  async ({ id }: { id: string; action: string; forceFetch?: boolean }, { rejectWithValue }) => {
    try {
      return await StageService.getStage(id);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ forceFetch, action }, { getState }) => {
      // force a refetch of the stage
      if (forceFetch) return true;

      const state = getState() as IStoreState;
      const { meta } = state.data;

      // do not refetch if the stage is already in the store
      return !has(meta, action);
    },
  }
);

export const deleteStage = createAsyncThunk(
  VENUE_ACTIONS.deleteStage,
  async (stageId: string, { rejectWithValue }) => {
    try {
      await StageService.deleteStage(stageId);
      return { path: API_ENDPOINTS.STAGE(stageId), dataKey: IDataKeys.stage };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editStage = createAsyncThunk(
  VENUE_ACTIONS.editStage,
  async ({ stageId, stageData }: { stageId: string; stageData: IStage }, { rejectWithValue }) => {
    try {
      return await StageService.editStage(stageId, stageData);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createStageCapacity = createAsyncThunk(
  VENUE_ACTIONS.createStageCapacity,
  async (
    { stagePath, capacity }: { stagePath: string; capacity: IStageCapacity },
    { rejectWithValue }
  ) => {
    try {
      const stageCapacity: any = await StageService.createCapacity(stagePath, capacity);
      const id = (Object.values(stageCapacity?.stageCapacity) as any)?.[0]?.attributes?.id;
      return { id };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteStageCapacity = createAsyncThunk(
  VENUE_ACTIONS.deleteStageCapacity,
  async ({ id }: { stageId: string; id: string }, { rejectWithValue }) => {
    try {
      await StageService.deleteCapacity(id);
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editStageCapacity = createAsyncThunk(
  VENUE_ACTIONS.editStageCapacity,
  async (
    {
      capacityId,
      capacityData,
    }: { stageId: string; capacityId: string; capacityData: IStageCapacity },
    { rejectWithValue }
  ) => {
    try {
      return await StageService.editCapacity(capacityId, capacityData);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

interface IEditStageEquipmentCategoriesData {
  stageId: string;
  category: string;
  stageData: IStagePatchData;
}

export const dispatchEditStageEquipmentCategoriesAction = (
  dispatch: Dispatch<any>,
  params: IEditStageEquipmentCategoriesData
) => {
  const action = getEquipmentCategoriesKey(params.stageId, params.category);

  return dispatch(editStageEquipmentCategories({ action, params }));
};

export const editStageEquipmentCategories = createAsyncThunk(
  VENUE_ACTIONS.editStageEquipmentCategories,
  async (
    {
      params: { stageId, stageData },
    }: { params: IEditStageEquipmentCategoriesData; action: string },
    { rejectWithValue }
  ) => {
    try {
      return await StageService.editStage(stageId, stageData);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

interface IGetStageEquipmentData {
  venue: string;
  stage: string;
  category: IEquipmentCategory;
  page?: number;
}

export const dispatchGetStageEquipmentAction = (
  dispatch: Dispatch<any>,
  params: IGetStageEquipmentData
) => {
  const paginationKey = getStageEquipmentKey(params.stage, params.category);

  return dispatch(getStageEquipment({ paginationKey, params, page: params.page || 1 }));
};

export const dispatchForceGetStageEquipmentAction = (
  dispatch: Dispatch<any>,
  params: IGetStageEquipmentData
) => {
  const { venue, stage, category } = params;

  dispatch(clearActionPagination(getStageEquipmentKey(stage, category)));

  dispatchGetStageEquipmentAction(dispatch, {
    venue: venue,
    stage: stage,
    category: category,
    page: 1,
  });
};

export const getStageEquipment = createAsyncThunk(
  VENUE_ACTIONS.getStageEquipment,
  async (
    { params }: { params: IGetStageEquipmentData; page: number; paginationKey: string },
    { rejectWithValue }
  ) => {
    try {
      return await EquipmentService.getEquipment(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ paginationKey, page }, { getState }) => {
      const state = getState() as IStoreState;

      // do not refetch if equipment page has already been fetched
      return !isPageFetchedInState(state, paginationKey, page);
    },
  }
);
